import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div
        className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{title}</span>
        <span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const VENDOR_TYPES = ["Individual", "Company", "Government", "NGO", "Other"];
const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const PAYMENT_TERMS = ["Net 15", "Net 30", "Net 45", "Due on receipt"];
const STATUSES = ["Active", "Inactive", "Blacklisted"];
const ADDRESS_TYPES = ["Billing", "Shipping", "Office", "Warehouse", "Other"];
const ACCOUNT_TYPES = ["Savings", "Current", "Overdraft", "Cash Credit"];

const emptyAddress = () => ({ addressType: "Billing", addressLine1: "", addressLine2: "", city: "", state: "", country: "", postalCode: "" });

const initialForm = {
  name: "",
  contactPerson: "",
  vendorType: "Company",
  industry: "",
  website: "",
  phone: "",
  email: "",
  gstNumber: "",
  status: "Active",
  currency: "INR",
  paymentTerms: "Net 30",
  bankName: "",
  accountHolderName: "",
  accountNumber: "",
  ifscCode: "",
  swiftCode: "",
  branchName: "",
  accountType: "",
  addresses: [emptyAddress()],
};

export default function NewVendor() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("addresses");
  const [navbarColor, setNavbarColor] = useState("#080235");
  const refs = useRef({});

  const setRef = (name) => (element) => { refs.current[name] = element; };

  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  useEffect(() => {
    if (!token) { toast.error("You must be logged in."); navigate("/"); }
  }, [navigate, token]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => {});
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleAddressChange = (idx, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.addresses];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, addresses: updated };
    });
  };

  const addAddress = () => setFormData((prev) => ({ ...prev, addresses: [...prev.addresses, emptyAddress()] }));
  const removeAddress = (idx) => setFormData((prev) => ({ ...prev, addresses: prev.addresses.filter((_, i) => i !== idx) }));

  const inputClass = (field) => {
    if (errors[field]) return "form-control is-invalid";
    if (touched[field]) return "form-control is-valid";
    return "form-control";
  };

  const selectClass = (field) => {
    if (errors[field]) return "form-select is-invalid";
    if (touched[field]) return "form-select is-valid";
    return "form-select";
  };

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Vendor name is required.";
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Enter a valid email.";
    if (formData.phone && !/^[0-9+\-() ]{7,20}$/.test(formData.phone)) e.phone = "Enter a valid phone number.";
    if (formData.gstNumber && formData.gstNumber.length < 8) e.gstNumber = "GST number looks too short.";
    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) e.website = "Website must start with http:// or https://";
    return e;
  };

  const focusFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    const el = refs.current[firstKey];
    if (el?.focus) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
  };

  const submitVendor = async (redirectToList) => {
    const nextErrors = validate();
    setErrors(nextErrors);
    setTouched(Object.fromEntries(Object.keys(initialForm).map(k => [k, true])));

    if (Object.keys(nextErrors).length > 0) {
      focusFirstError(nextErrors);
      toast.error("Please fix the form errors before saving.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_BASE_URL}/api/vendors`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Vendor created successfully.");
      setFormData(initialForm);
      setErrors({});
      setTouched({});
      if (redirectToList) navigate("/vendors");
    } catch (err) {
      toast.error(err.message || "Failed to create vendor.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
        <div className="mb-3">
          <h4 className="m-0"><i className="bi bi-building-add me-2"></i> New Vendor</h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <button type="button" className="btn btn-primary me-2" disabled={submitting} onClick={() => submitVendor(true)}>
                {submitting ? "Saving..." : "Save"}
              </button>
              <button type="button" className="btn btn-outline-primary btn-sm me-2" disabled={submitting} onClick={() => submitVendor(false)}>
                Save & Add Another
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
            </div>
            <a href="/vendors" className="text-primary text-decoration-none me-3">List</a>
          </div>
        </div>

        <form>
          <Section title="Basic Information">
            <div className="col-md-4">
              <label className="form-label">Vendor Name <span className="text-danger">*</span></label>
              <input ref={setRef("name")} type="text" name="name" className={inputClass("name")} value={formData.name} onChange={handleChange} placeholder="e.g. Balaji Technologies" />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Contact Person</label>
              <input ref={setRef("contactPerson")} type="text" name="contactPerson" className={inputClass("contactPerson")} value={formData.contactPerson} onChange={handleChange} placeholder="Primary contact name" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Vendor Type</label>
              <select name="vendorType" className={selectClass("vendorType")} value={formData.vendorType} onChange={handleChange}>
                {VENDOR_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Industry</label>
              <select name="industry" className="form-select" value={formData.industry} onChange={handleChange}>
                <option value="">Select industry…</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Website</label>
              <input ref={setRef("website")} type="url" name="website" className={inputClass("website")} value={formData.website} onChange={handleChange} placeholder="https://example.com" />
              {errors.website && <div className="invalid-feedback">{errors.website}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">GST Number</label>
              <input ref={setRef("gstNumber")} type="text" name="gstNumber" className={inputClass("gstNumber")} value={formData.gstNumber} onChange={handleChange} placeholder="GST / tax registration number" />
              {errors.gstNumber && <div className="invalid-feedback">{errors.gstNumber}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Status</label>
              <select name="status" className={selectClass("status")} value={formData.status} onChange={handleChange}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </Section>

          <Section title="Contact Details">
            <div className="col-md-4">
              <label className="form-label">Phone</label>
              <input ref={setRef("phone")} type="text" name="phone" className={inputClass("phone")} value={formData.phone} onChange={handleChange} placeholder="+91 98765 43210" />
              {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Email</label>
              <input ref={setRef("email")} type="email" name="email" className={inputClass("email")} value={formData.email} onChange={handleChange} placeholder="vendor@example.com" />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
          </Section>

          <ul className="nav nav-tabs mb-0 fw-semibold" style={{ backgroundColor: navbarColor }}>
            {[["addresses", "Address Details"], ["financial", "Financial"]].map(([key, label]) => (
              <li className="nav-item" key={key}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  onClick={() => setActiveTab(key)}
                  style={{
                    color: activeTab === key ? navbarColor : "#fff",
                    backgroundColor: activeTab === key ? "#fff" : "transparent",
                    fontWeight: activeTab === key ? "600" : "normal",
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
          <div className="border p-3 mb-3">
            {activeTab === "addresses" && (
              <Section title="Address Details">
                {formData.addresses.map((addr, idx) => (
                  <div key={idx} className="col-12 border rounded p-0 mb-2 overflow-hidden">
                    <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #dee2e6" }}>
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-secondary">{addr.addressType || "Billing"}</span>
                        <span className="text-muted small">Address {idx + 1}</span>
                      </div>
                      {formData.addresses.length > 1 && (
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeAddress(idx)}>Remove</button>
                      )}
                    </div>
                    <div className="row g-2 p-3">
                      <div className="col-md-3">
                        <label className="form-label">Address Type</label>
                        <select className="form-select" value={addr.addressType} onChange={(e) => handleAddressChange(idx, "addressType", e.target.value)}>
                          {ADDRESS_TYPES.map(t => <option key={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="col-md-9">
                        <label className="form-label">Address Line 1</label>
                        <input type="text" className="form-control" value={addr.addressLine1} onChange={(e) => handleAddressChange(idx, "addressLine1", e.target.value)} placeholder="Street address" />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Address Line 2</label>
                        <input type="text" className="form-control" value={addr.addressLine2} onChange={(e) => handleAddressChange(idx, "addressLine2", e.target.value)} placeholder="Apartment, suite, building (optional)" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">City</label>
                        <input type="text" className="form-control" value={addr.city} onChange={(e) => handleAddressChange(idx, "city", e.target.value)} placeholder="City" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">State / Province</label>
                        <input type="text" className="form-control" value={addr.state} onChange={(e) => handleAddressChange(idx, "state", e.target.value)} placeholder="State" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Country</label>
                        <input type="text" className="form-control" value={addr.country} onChange={(e) => handleAddressChange(idx, "country", e.target.value)} placeholder="Country" />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Postal Code</label>
                        <input type="text" className="form-control" value={addr.postalCode} onChange={(e) => handleAddressChange(idx, "postalCode", e.target.value)} placeholder="PIN / ZIP" />
                      </div>
                    </div>
                  </div>
                ))}
                <div className="col-12">
                  <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={addAddress}>+ Add Address</button>
                </div>
              </Section>
            )}
            {activeTab === "financial" && (
              <Section title="Financial">
                <div className="col-md-4">
                  <label className="form-label">Currency</label>
                  <select name="currency" className={selectClass("currency")} value={formData.currency} onChange={handleChange}>
                    {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Payment Terms</label>
                  <select name="paymentTerms" className="form-select" value={formData.paymentTerms} onChange={handleChange}>
                    {PAYMENT_TERMS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div className="col-12"><hr className="my-1" /><strong className="text-muted" style={{ fontSize: 13 }}>Bank / Payment Details</strong></div>
                <div className="col-md-4">
                  <label className="form-label">Bank Name</label>
                  <input type="text" name="bankName" className="form-control" value={formData.bankName} onChange={handleChange} placeholder="e.g. State Bank of India" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Account Holder Name</label>
                  <input type="text" name="accountHolderName" className="form-control" value={formData.accountHolderName} onChange={handleChange} placeholder="Name on account" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Account Type</label>
                  <select name="accountType" className="form-select" value={formData.accountType} onChange={handleChange}>
                    <option value="">Select…</option>
                    {ACCOUNT_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Account Number</label>
                  <input type="text" name="accountNumber" className="form-control" value={formData.accountNumber} onChange={handleChange} placeholder="Bank account number" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">IFSC Code</label>
                  <input type="text" name="ifscCode" className="form-control" value={formData.ifscCode} onChange={handleChange} placeholder="e.g. SBIN0001234" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">SWIFT / BIC Code</label>
                  <input type="text" name="swiftCode" className="form-control" value={formData.swiftCode} onChange={handleChange} placeholder="For international transfers" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Branch Name</label>
                  <input type="text" name="branchName" className="form-control" value={formData.branchName} onChange={handleChange} placeholder="Branch name or address" />
                </div>
              </Section>
            )}
          </div>
        </form>

        <div>
          <button type="button" className="btn btn-primary me-2" disabled={submitting} onClick={() => submitVendor(true)}>
            {submitting ? "Saving..." : "Save"}
          </button>
          <button type="button" className="btn btn-outline-primary btn-sm me-2" disabled={submitting} onClick={() => submitVendor(false)}>
            Save & Add Another
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </div>
    </>
  );
}
